import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';

type SnmpConfig = {
  v1?: boolean;
  v2c?: boolean;
  v3?: boolean;
  read_community?: string;
  write_community?: string;
  engineID?: string;
};

type TrapConfig = {
  enabled?: boolean;
  destination?: string;
  community?: string;
};

type SnmpUser = {
  name?: string;
  user?: string;
};

function userName(u: SnmpUser): string {
  return String(u.name ?? u.user ?? '');
}

export default function WebUiSettingsSnmpPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [config, setConfig] = React.useState<SnmpConfig>({});
  const [trap, setTrap] = React.useState<TrapConfig>({});
  const [users, setUsers] = React.useState<SnmpUser[]>([]);
  const [newUser, setNewUser] = React.useState('operator');
  const [authPass, setAuthPass] = React.useState('');
  const [privPass, setPrivPass] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [cfg, traps, u] = await Promise.all([
      postWebUiApi<SnmpConfig>({
        service: 'snmp',
        access: 'get',
        command: 'get config',
      }),
      postWebUiApi<TrapConfig>({
        service: 'trap',
        access: 'get',
        command: 'get trap',
      }),
      postWebUiApi<SnmpUser[]>({
        service: 'snmp',
        access: 'get',
        command: 'get user',
      }),
    ]);
    setConfig(cfg ?? {});
    setTrap(traps ?? {});
    setUsers(u ?? []);
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  return (
    <WebUiPageWrapper titleList={['Settings', 'SNMP']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-row gap-2">
        <div className="flex w-1/3 flex-col gap-2">
          <h4 className="font-bold">Server Settings</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Setting</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Version 1</td>
                <td className="text-center">
                  <WebUiInput
                    inputSize="sm"
                    type="checkbox"
                    disabled={readonly}
                    checked={Boolean(config.v1)}
                    onChange={(e) =>
                      setConfig((p) => ({ ...p, v1: e.target.checked }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Version 2c</td>
                <td className="text-center">
                  <WebUiInput
                    inputSize="sm"
                    type="checkbox"
                    disabled={readonly}
                    checked={Boolean(config.v2c)}
                    onChange={(e) =>
                      setConfig((p) => ({ ...p, v2c: e.target.checked }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Version 3</td>
                <td className="text-center">
                  <WebUiInput
                    inputSize="sm"
                    type="checkbox"
                    disabled={readonly}
                    checked={Boolean(config.v3)}
                    onChange={(e) =>
                      setConfig((p) => ({ ...p, v3: e.target.checked }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-head">
                <th colSpan={2}>Version 1 / 2c Settings</th>
              </tr>
              <tr className="webui-table-row-content">
                <td>Read Community</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    disabled={readonly}
                    value={config.read_community ?? ''}
                    onChange={(e) =>
                      setConfig((p) => ({
                        ...p,
                        read_community: e.target.value,
                      }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Write Community</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    disabled={readonly}
                    value={config.write_community ?? ''}
                    onChange={(e) =>
                      setConfig((p) => ({
                        ...p,
                        write_community: e.target.value,
                      }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Engine ID</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    disabled
                    value={config.engineID ?? ''}
                    readOnly
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div>
            <WebUiButton
              type="button"
              disabled={readonly}
              onClick={async () => {
                try {
                  await postWebUiApi({
                    service: 'snmp',
                    access: 'set',
                    command: 'set config',
                    parameters: {
                      v1: Boolean(config.v1),
                      v2c: Boolean(config.v2c),
                      v3: Boolean(config.v3),
                      read_community: config.read_community ?? '',
                      write_community: config.write_community ?? '',
                      engineID: config.engineID ?? '',
                    },
                  });
                  setMessage('SNMP server settings updated.');
                  setError(null);
                } catch (err) {
                  setError((err as Error).message);
                }
              }}
            >
              Set Server Settings
            </WebUiButton>
          </div>
        </div>

        <div className="flex w-1/3 flex-col gap-2">
          <h4 className="font-bold">Add User</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Parameter</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Username</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    disabled={readonly}
                    value={newUser}
                    onChange={(e) => setNewUser(e.target.value)}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Authpass</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    type="password"
                    disabled={readonly}
                    value={authPass}
                    onChange={(e) => setAuthPass(e.target.value)}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Privpass</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    type="password"
                    disabled={readonly}
                    value={privPass}
                    onChange={(e) => setPrivPass(e.target.value)}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div>
            <WebUiButton
              type="button"
              disabled={
                readonly ||
                !newUser ||
                authPass.length < 8 ||
                privPass.length < 8
              }
              onClick={async () => {
                try {
                  await postWebUiApi({
                    service: 'snmp',
                    access: 'set',
                    command: 'add user',
                    parameters: [
                      { name: newUser, authpass: authPass, privpass: privPass },
                    ],
                  });
                  setMessage('SNMP user added.');
                  setError(null);
                  await reload();
                } catch (err) {
                  setError((err as Error).message);
                }
              }}
            >
              Add User
            </WebUiButton>
          </div>
          <h4 className="mt-2 font-bold">SNMP Version 3 Users</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>User</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const name = userName(u);
                return (
                  <tr key={name} className="webui-table-row-content">
                    <td>{name}</td>
                    <td className="text-center">
                      <WebUiButton
                        disabled={readonly}
                        onClick={async () => {
                          try {
                            await postWebUiApi({
                              service: 'snmp',
                              access: 'set',
                              command: 'remove user',
                              parameters: [name],
                            });
                            setMessage('SNMP user removed.');
                            setError(null);
                            await reload();
                          } catch (err) {
                            setError((err as Error).message);
                          }
                        }}
                      >
                        Remove
                      </WebUiButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex w-1/3 flex-col">
          <h4 className="mb-2 font-bold">Traps</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Parameter</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Enabled</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    type="checkbox"
                    disabled={readonly}
                    checked={Boolean(trap.enabled)}
                    onChange={(e) =>
                      setTrap((p) => ({ ...p, enabled: e.target.checked }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Receiver</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    disabled={readonly}
                    value={trap.destination ?? ''}
                    onChange={(e) =>
                      setTrap((p) => ({ ...p, destination: e.target.value }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Trap Community</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    disabled={readonly}
                    value={trap.community ?? ''}
                    onChange={(e) =>
                      setTrap((p) => ({ ...p, community: e.target.value }))
                    }
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div className="mt-2 flex w-full flex-row gap-2">
            <WebUiButton
              disabled={readonly}
              onClick={async () => {
                try {
                  await postWebUiApi({
                    service: 'trap',
                    access: 'set',
                    command: 'set trap',
                    parameters: {
                      version: 'v1',
                      destination: trap.destination ?? '',
                      enabled: Boolean(trap.enabled),
                      community: trap.community ?? '',
                    },
                  });
                  setMessage('SNMP trap settings updated.');
                  setError(null);
                } catch (err) {
                  setError((err as Error).message);
                }
              }}
            >
              Set SNMP Trap Settings
            </WebUiButton>
            <WebUiButton
              onClick={() =>
                reload().catch((err) => setError((err as Error).message))
              }
            >
              Reload
            </WebUiButton>
          </div>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
